/**
 * Phase 6a.23 — derive the set of foreign currencies an engagement
 * needs `closingFxRates` for.
 *
 * Backend rule (Spec §20.3): every contract whose `currency !==
 * functionalCurrency` requires a matching
 * `engagement.closingFxRates[contract.currency]`. The frontend mirrors
 * this by scanning the live `contracts[]` array.
 *
 * Phase 6e.4.2-hotfix-2 — soft-delete is gone (Bug D). The engagement-
 * aware overload no longer filters out soft-deleted slots because no
 * such slots exist: the contracts array IS the list of live slots.
 *
 * Returns currencies sorted alphabetically — predictable rendering
 * order means screen reader / dark-mode / RTL flows all behave the
 * same regardless of slot insertion order.
 */

import type { ContractInput, EngagementHistoryEntry } from "@/types";

/**
 * BB4 / BJ — single source of truth for the "Day-1 seed" currencies
 * that the engagement form pre-populates in the Closing FX Rates
 * section once `auditPeriodEnd` is set. Auditors almost always need
 * USD/EUR/GBP rates regardless of which contracts they end up adding,
 * so seeding them up-front eliminates a round-trip through the
 * contract list before any contract can be saved.
 *
 * Keep this list short and conservative — the goal is "covers >95 %
 * of engagements without noise", not "every ISO 4217 currency".
 * Additional currencies surface automatically once a contract that
 * uses them is added (via `deriveFxCurrencies`).
 */
export const MAJOR_CURRENCIES = ["USD", "EUR", "GBP"] as const;
export type MajorCurrency = (typeof MAJOR_CURRENCIES)[number];

export function deriveFxCurrencies(
  contracts: ReadonlyArray<ContractInput | null>,
  functionalCurrency: string,
): string[] {
  const set = new Set<string>();
  for (const c of contracts) {
    if (!c) continue;
    const ccy = c.currency?.trim();
    if (!ccy || ccy === functionalCurrency) continue;
    set.add(ccy);
  }
  return Array.from(set).sort();
}

export function deriveFxCurrenciesFromEngagement(
  engagement: Pick<
    EngagementHistoryEntry,
    "contracts" | "functionalCurrency"
  >,
): string[] {
  return deriveFxCurrencies(
    engagement.contracts,
    engagement.functionalCurrency,
  );
}
